import "./Admin-login.css";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../components/loadingSpinner/LoadingSpinner";
import { clearSession } from "../../utils/session";

function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [waitingForLogin, setWaitingForLogin] = useState(false);
  const [waitingForLoginVerification, setWaitingForLoginVerification] =
    useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setWaitingForLoginVerification(false);
        return;
      }

      // token verification
      const apiUrl = `${process.env.REACT_APP_BASE_API_URL}/admin/verify_login`;
      const fData = new FormData();
      fData.append("token", token);
      await axios
        .post(apiUrl, fData)
        .then((res) => {
          if (res.data?.type === "success") {
            localStorage.setItem("user", JSON.stringify(res.data.payload));
            navigate("/welcomeAdmin");
          } else {
            clearSession();
            toast.error("An unknown behavior has occurred");
            setWaitingForLoginVerification(false);
          }
        })
        .catch((error) => {
          // Only a 401 means the session is over. A server error or a dropped
          // connection used to sign the admin out as well; now the token
          // stays, and signing in again or reloading picks it up.
          if (error.response?.status === 401) clearSession();
          const payload = error.response?.data?.payload;
          toast.error(
            !error.response
              ? "We couldn't reach Sourship. Check your connection and try again."
              : (typeof payload === "string" && payload) || "We couldn't check your session just now. Try again in a moment."
          );
          setWaitingForLoginVerification(false);
        });
    };

    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authenticateUser = (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password are required");
      return false;
    }
    setWaitingForLogin(true);
    const apiUrl = `${process.env.REACT_APP_BASE_API_URL}/admin/login`;
    const fData = new FormData();
    fData.append("email", email);
    fData.append("password", password);

    axios
      .post(apiUrl, fData)
      .then((res) => {
        if (res.data?.type === "success" && res.data?.payload) {
          // The previous account's session and cached data go first, then the
          // new token is written, and only then does the app move on: the
          // first page must not start its requests without a token.
          clearSession();
          localStorage.setItem("token", res.data.payload.token);
          localStorage.setItem("user", JSON.stringify(res.data.payload.user));
          navigate("/welcomeAdmin");
        } else {
          clearSession();
          toast.error("Something unexpected happened. Please try again.");
        }
        setWaitingForLogin(false);
      })
      .catch((error) => {
        toast.error(error.response?.data?.payload || error.message);
        setWaitingForLogin(false);
      });
  };

  if (waitingForLoginVerification) {
    return (
      <div className="login-page">
        <div className="login-glow" aria-hidden="true"></div>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-glow" aria-hidden="true"></div>

      <div className="login-container">
        <aside className="login-art-side">
          <div className="login-brand">
            <span className="login-brand-mark">S</span>
            Sourship
          </div>

          <div className="art-content">
            <h1>Admin <em>control panel</em></h1>
            <p>
              Sourcing, orders, inventory and settlements for every seller on the
              platform — in one place.
            </p>
          </div>

          <ul className="art-facts">
            <li><i className="fas fa-clipboard-list"></i> Sourcing &amp; restock queues</li>
            <li><i className="fas fa-shopping-cart"></i> Order lifecycle management</li>
            <li><i className="fas fa-wallet"></i> Wallet &amp; ledger reconciliation</li>
          </ul>
        </aside>

        <section className="login-form-side">
          <div className="form-container">
            <h2>Welcome back</h2>
            <p className="welcome-text">Log in to access the admin dashboard.</p>

            <form onSubmit={authenticateUser}>
              <div className="form-group">
                <label className="field-label" htmlFor="email">Email</label>
                <div className="field-wrap">
                  <i className="fas fa-user"></i>
                  <input
                    type="text"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="field-label" htmlFor="password">Password</label>
                <div className="field-wrap">
                  <i className="fas fa-lock"></i>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="off"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-gradient btn-lg btn-block"
                disabled={waitingForLogin}
              >
                <span>{waitingForLogin ? "Logging in" : "Log in"}</span>
                <i className={`fas ${waitingForLogin ? "fa-rotate spinning" : "fa-arrow-right"}`}></i>
              </button>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
}

export default AdminLogin;
